import React from 'react';
import { useSWRConfig } from 'swr';
import { startCase } from 'lodash';
import { useParams } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';

import { Bank } from './bank';
import { TeamInventory } from './team-inventory';
import {
  TeamGiveItemAsync,
  TeamRemoveItemAsync
} from '../../../../../../utils/game-engine/team';
import {
  UpdateTeamStateAsync,
  UpdatePlayerStateAsync
} from '../../../../../../utils/game-engine/base';
import {
  useGetBank,
  useBankAction
} from '../../../../../../hooks/use-inventory-bank';
import {
  AddItem,
  RemoveItem
} from '../../../../../../utils/game-engine/assets';
import { PlayerInventory } from './player-inventory';
import {
  CastTeamInventory,
  GetItemAchievement
} from '../../../../../../utils/game-engine/inventory';
import { Card } from '../../../../../../components/card';
import { TeamContext } from '../../../../../../contexts/team';
import { PlayerContext } from '../../../../../../contexts/player';
import { OverlayContext } from '../../../../../../contexts/overlay';
import { BankItem } from '../../../../../../types/responses/bank-response';
import { Item } from '../../../../../../types/game-document/entities/item';
import { GameDocumentContext } from '../../../../../../contexts/game-document';
import { EnterGameButton } from '../../../../../../components/enter-game-button';
import { DisplayLanguageContext } from '../../../../../../contexts/display-languages';
import AchievementDialog from '../../../../../../components/dialog/achievement-dialog';
import { generateTitleById } from '../../../../../../utils/game-document/display-languages';

export interface ItemAchievement extends Item {
  titleRes: string;
  imageRes: string;
  summaryRes: string;
  quantity: number;
}

export const InventoryCard: React.FC<{
  items: BankItem[];
  item: ItemAchievement;
  action: 'acquire' | 'return';
  isTeamGame: boolean;
  setItemCard: React.Dispatch<
    React.SetStateAction<ItemAchievement | undefined>
  >;
}> = ({ item, items, action, isTeamGame, setItemCard }) => {
  const { gameCode } = useParams();
  const { mutate } = useSWRConfig();

  const [gameDocument] = React.useContext(GameDocumentContext);
  const [playerState, setPlayer] = React.useContext(PlayerContext);
  const [teamState, setTeam] = React.useContext(TeamContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);

  const { isMutating, trigger } = useBankAction(gameCode ?? '');

  const bankItem = items.find((bankItem) => bankItem.itemId === item.id);
  const bankItemGroups = gameDocument.gameDocument?.assets.bankItemGroups ?? [];

  const inventoryItems = gameDocument.gameDocument?.assets.items ?? [];
  const inventoryItem = GetItemAchievement(
    gameDocument.gameDocument!,
    playerState?.playerState?.language?.name! ?? '',
    item.id,
    // if the item not found in the bank
    // set it as -1, which means unlimited stock
    bankItem ? bankItem.currentStock : item.quantity ? item.quantity : -1
  );

  const stock = React.useMemo(() => {
    if (bankItem) return ` (${bankItem.currentStock})`;
    if (item.quantity) return ` (${item.quantity})`;

    return '';
  }, [bankItem, item]);

  const isDisableByGroupLimit = () => {
    if (!item.useBank || !item.bankItemGroupId || action === 'return')
      return false;

    const selectedGroup = bankItemGroups.find(
      (g) => g.id === item.bankItemGroupId
    );

    if (!selectedGroup) return false;

    const itemGroupIds = inventoryItems
      .filter((i) => i.bankItemGroupId === selectedGroup.id)
      .map((i) => i.id);

    const itemGroupOwned = (
      (isTeamGame
        ? teamState.teamState?.inventory
        : playerState.playerState?.inventory) ?? []
    )
      .filter((i) => itemGroupIds.includes(i.id))
      .reduce((prev, current) => prev + current.quantity, 0);

    return itemGroupOwned >= selectedGroup.maxItemsPerPlayer;
  };

  const bankItemAction = async (itemId: string) => {
    const item = await trigger({
      itemId,
      action,
      quantity: 1,
      actor: playerState.playerState?.name ?? '',
      actorId: playerState.playerState?.code ?? ''
    });

    if (item.success) {
      if (isTeamGame) {
        setTeam((prev) => {
          const response =
            action === 'acquire'
              ? TeamGiveItemAsync(prev, item.itemId, item.requestedQuantity)
              : TeamRemoveItemAsync(prev, item.itemId, item.requestedQuantity);
          return UpdateTeamStateAsync(prev, response);
        });
      } else {
        setPlayer((prev) => {
          const payload = {
            id: item.itemId,
            quantity: item.requestedQuantity
          };

          const response =
            action === 'acquire'
              ? AddItem(prev, payload)
              : RemoveItem(prev, payload);
          return UpdatePlayerStateAsync(prev, response);
        });
      }
    }

    mutate(`games/${gameCode}/bank`);
  };

  return (
    <div
      className={'d-flex flex-column position-relative gap-2'}
      style={{
        position: 'relative'
      }}>
      {bankItem && bankItem.currentStock === 0 && (
        <div
          style={{
            top: 5,
            right: 5,
            color: 'white',
            display: 'flex',
            padding: '2px 4px',
            fontSize: '12px',
            borderRadius: '1rem',
            position: 'absolute',
            alignItems: 'center',
            backgroundColor: 'red',
            justifyContent: 'center'
          }}>
          {generateTitleById(
            'c6d7e8f9-0a1b-2c3d-4e5f-6a7b8c9d0e1f',
            gameDocument,
            displayLanguageContext.displayLanguageSelected.resources!,
            'game'
          ) || 'Out of stock'}
        </div>
      )}
      <Card
        className={'cursor-pointer'}
        imageUrl={inventoryItem.imageRes}
        title={`${inventoryItem.titleRes ?? ''}${stock}`}
        subTitle={''}
        onClick={() => setItemCard(inventoryItem)}
      />
      {item.useBank && (
        <Button
          size={'small'}
          themeColor={'primary'}
          fillMode={'outline'}
          disabled={
            isMutating ||
            bankItem?.currentStock === 0 ||
            isDisableByGroupLimit()
          }
          onClick={() => bankItemAction(item.id)}>
          {action === 'acquire'
            ? generateTitleById(
                'd7e8f90a-1b2c-3d4e-5f6a-7b8c9d0e1f2a',
                gameDocument,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Acquire'
            : generateTitleById(
                'e8f90a1b-2c3d-4e5f-6a7b-8c9d0e1f2a3b',
                gameDocument,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Return'}
        </Button>
      )}
    </div>
  );
};

export const Inventory = () => {
  const { gameCode } = useParams();

  const [gameDocument] = React.useContext(GameDocumentContext);
  const [teamState] = React.useContext(TeamContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);
  const [overlay] = React.useContext(OverlayContext);

  const [itemCard, setItemCard] = React.useState<ItemAchievement | undefined>();

  const { data: bank } = useGetBank(
    overlay.drawerIsOpen ? gameCode : undefined
  );

  const teamInventory = React.useMemo(() => {
    if (!teamState.teamState) return [];

    return CastTeamInventory(gameDocument.gameDocument!, teamState.teamState!);
  }, [teamState.teamState]);

  return (
    <>
      <Container className={'drawer__body'} fluid>
        <div
          className={
            'd-flex flex-column flex-grow-1 h-100 w-100 overflow-hidden'
          }>
          <Row className={'mb-4 align-items-center border-bottom pb-3'}>
            <Col>
              <h1 className={'m-0 fw-bold'}>
                {generateTitleById(
                  'f7a7c135-91e2-433f-a7ed-0b62ff39aba0',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Inventory'}
              </h1>
            </Col>
          </Row>

          <Row className={'h-100 w-100 m-0 p-0 overflow-hidden'}>
            <Col className={'p-0 ml-0 mr-2 h-100 d-flex flex-column gap-3'}>
              <PlayerInventory
                bank={bank}
                setItemCard={setItemCard}
                teamInventory={teamInventory.length > 0}
              />

              {teamInventory.length > 0 && (
                <TeamInventory
                  bank={bank}
                  setItemCard={setItemCard}
                  teamInventory={teamInventory}
                />
              )}
            </Col>

            <Bank bank={bank} setItemCard={setItemCard} />
          </Row>
        </div>
      </Container>
      <EnterGameButton />

      {itemCard && (
        <AchievementDialog
          imageUrl={itemCard.imageRes}
          onMaskClick={() => setItemCard(undefined)}
          onClose={() => setItemCard(undefined)}>
          {itemCard.quantity !== -1 && <h1>{itemCard.quantity}</h1>}
          <h2>{itemCard.titleRes}</h2>
          <p>{itemCard.summaryRes}</p>
        </AchievementDialog>
      )}
    </>
  );
};
